// Product catalog — every dedicated /funding/* page is driven by this file.
// Each entry is self-contained content so SEO copy is unique per product.

export const PRODUCTS = {
  "working-capital": {
    slug: "working-capital",
    homeTitle: "Working Capital",
    homeDesc: "Flexible capital from $25K–$5M to handle payroll, inventory, marketing, or growth.",
    icon: "CircleDollarSign",
    seoTitle: "Working Capital Loans for Small Business | Capless Funding",
    seoDescription: "Fast working capital loans from $25K–$5M for small businesses. Apply in minutes. Same-day funding available. No credit pull, no credit score required.",

    heroEyebrow: "Working Capital",
    heroHeadline: "Fast working capital to keep your business moving.",
    heroSubhead:
      "Get $25K–$5M in working capital to cover payroll, inventory, taxes, seasonal cash flow — anything your business needs. Apply in minutes, funded as fast as the same day.",
    applyProductValue: "Working Capital",

    keyFacts: [
      { label: "Amounts", value: "$25K – $5M" },
      { label: "Terms", value: "3 – 24 months" },
      { label: "Speed", value: "Same-day funding" },
      { label: "Credit pull", value: "None — ever" },
    ],

    useCases: [
      "Cover payroll during a slow month",
      "Stock up on inventory before peak season",
      "Pay an unexpected tax bill",
      "Fund a marketing campaign with quick ROI",
      "Bridge a gap between accounts receivable",
      "Take on a larger contract you couldn't afford before",
    ],

    requirements: [
      "6+ months in business",
      "$20,000+ in monthly revenue",
      "Business checking account",
      "No credit score required",
    ],

    why: [
      {
        title: "Approval in hours, not weeks",
        desc: "Most working capital files are approved within 4–24 hours of a complete application.",
      },
      {
        title: "Direct lender, not a broker",
        desc: "We underwrite from our own balance sheet — your file stays with our team, end to end.",
      },
      {
        title: "Renew & grow with us",
        desc: "Pay down your balance and we can refresh or upsize your capital as your business scales.",
      },
    ],

    faqs: [
      {
        q: "How much working capital can I qualify for?",
        a: "Most clients qualify for 5–15% of their annual revenue. Try our pre-qualify tool on the home page for a personalized estimate.",
      },
      {
        q: "How fast can I receive funds?",
        a: "After signing the contract, funds are typically wired within one business day — same-day funding is available for most working capital files.",
      },
      {
        q: "What can I use the working capital for?",
        a: "Any legitimate business expense: payroll, inventory, taxes, marketing, equipment, expansion, taking on a new contract — your choice.",
      },
      {
        q: "Will applying affect my credit?",
        a: "No. We do not pull your credit at any point during the application — no soft pull, no hard pull. We make our funding decisions based on your business performance, not your personal score.",
      },
    ],
  },

  "equipment-financing": {
    slug: "equipment-financing",
    homeTitle: "Equipment Financing",
    homeDesc: "Finance trucks, machinery, medical, and tech up to $2M. 100% financing available.",
    icon: "Truck",
    seoTitle: "Equipment Financing for Small Business | Capless Funding",
    seoDescription: "Finance up to $2M in business equipment — trucks, machinery, medical, restaurant, tech. 100% financing available with terms up to 84 months.",

    heroEyebrow: "Equipment Financing",
    heroHeadline: "Finance the equipment your business needs to grow.",
    heroSubhead:
      "Up to $2M in equipment financing with 100% financing available — no down payment required for qualified borrowers. Terms up to 84 months. The equipment itself is the collateral.",
    applyProductValue: "Equipment Financing",

    keyFacts: [
      { label: "Amounts", value: "Up to $2M" },
      { label: "Terms", value: "24 – 84 months" },
      { label: "Down payment", value: "0% – 20% (typical)" },
      { label: "Funding speed", value: "3 – 5 business days" },
    ],

    useCases: [
      "Add a new truck to your fleet",
      "Upgrade restaurant kitchen equipment",
      "Buy medical or dental imaging equipment",
      "Add CNC machines or shop equipment",
      "Finance computer hardware or software",
      "Construction equipment, forklifts, generators",
    ],

    requirements: [
      "1+ years in business",
      "$15,000+ in monthly revenue",
      "Equipment quote or invoice from a vendor",
      "No credit score required",
    ],

    why: [
      {
        title: "100% financing available",
        desc: "Strong files can finance the entire cost of equipment — no down payment, just monthly payments.",
      },
      {
        title: "Long terms keep payments low",
        desc: "Terms up to 84 months mean your equipment can start generating revenue before it's fully paid for.",
      },
      {
        title: "Equipment is the collateral",
        desc: "Because the equipment secures the loan, rates are typically lower than unsecured working capital.",
      },
    ],

    faqs: [
      {
        q: "What types of equipment can I finance?",
        a: "Almost any business equipment with a useful life over 2 years: vehicles, machinery, medical/dental, restaurant, tech, construction, manufacturing, agricultural, and more.",
      },
      {
        q: "Can I finance used equipment?",
        a: "Yes. We finance both new and used equipment from dealers, manufacturers, or private sellers. Older equipment may have shorter terms.",
      },
      {
        q: "Do I need a down payment?",
        a: "For qualified borrowers we offer 100% financing with $0 down. Less-established businesses or weaker credit profiles may need 10–20% down.",
      },
      {
        q: "How is this different from a working capital loan?",
        a: "Equipment financing is secured by the equipment itself, which means lower rates and longer terms than unsecured working capital. The downside: funds can only be used to buy that specific equipment.",
      },
    ],
  },

  "sba-loans": {
    slug: "sba-loans",
    homeTitle: "SBA Loans",
    homeDesc: "SBA 7(a) and 504 programs up to $5M. Long terms, low rates, government-backed.",
    icon: "Landmark",
    seoTitle: "SBA Loans 7(a) and 504 | Capless Funding",
    seoDescription: "Government-backed SBA loans up to $5M with the longest terms and lowest rates available. SBA 7(a) and 504 programs for small business.",

    heroEyebrow: "SBA Loans",
    heroHeadline: "Government-backed funding at the lowest rates available.",
    heroSubhead:
      "SBA 7(a) and SBA 504 loans up to $5M with terms from 10 to 25 years. Because they're partially guaranteed by the U.S. Small Business Administration, you get the best rates on the market.",
    applyProductValue: "SBA Loans",

    keyFacts: [
      { label: "Amounts", value: "Up to $5M (7a), $5.5M (504)" },
      { label: "Terms", value: "10 – 25 years" },
      { label: "Use of funds", value: "Real estate, equipment, working capital, acquisitions" },
      { label: "Process time", value: "30 – 60 days" },
    ],

    useCases: [
      "Buy commercial real estate for your business",
      "Refinance higher-interest business debt",
      "Acquire another business",
      "Major expansion or renovation",
      "Long-term working capital (7-10 year terms)",
      "Buy out a business partner",
    ],

    requirements: [
      "2+ years in business",
      "Profitable operations (positive cash flow)",
      "U.S.-based, for-profit business",
      "Owner equity / collateral may be required",
      "No credit score required",
    ],

    why: [
      {
        title: "Lowest rates you'll find anywhere",
        desc: "Because the SBA guarantees a portion of the loan, lenders can offer their lowest rates — often half what you'd pay on a conventional loan.",
      },
      {
        title: "Long terms for big moves",
        desc: "Real estate loans go up to 25 years; working capital and equipment up to 10 years. Lower monthly payments, more breathing room.",
      },
      {
        title: "We handle the paperwork",
        desc: "SBA loans require more documentation. Our team walks you through every form so you don't get lost.",
      },
    ],

    faqs: [
      {
        q: "What's the difference between SBA 7(a) and SBA 504?",
        a: "SBA 7(a) is the most flexible — use it for working capital, equipment, real estate, refinancing, or acquisitions, up to $5M. SBA 504 is specifically for major fixed assets (real estate, large equipment) up to $5.5M with even longer terms.",
      },
      {
        q: "Why does SBA take 30–60 days when working capital is same-day?",
        a: "SBA loans are partially guaranteed by the U.S. government, which means they require more documentation and a government review. The trade-off: significantly lower rates and longer terms.",
      },
      {
        q: "Do I need to put up collateral?",
        a: "For larger SBA loans (over ~$25K for 7(a)), the SBA requires lenders to take collateral when available. The good news: lack of collateral alone won't disqualify a strong file.",
      },
      {
        q: "Are SBA loans only for new businesses?",
        a: "No — SBA loans are available to most for-profit U.S. businesses. We typically need to see 2+ years of profitable operations to qualify.",
      },
    ],
  },

  "heloc": {
    slug: "heloc",
    homeTitle: "HELOC",
    homeDesc: "Tap your home equity to fuel business growth. Lines from $50K to $500K.",
    icon: "Home",
    seoTitle: "HELOC for Business Funding | Capless Funding",
    seoDescription: "Tap your home equity for business growth. HELOC lines of credit from $50K to $500K with draw-as-you-need flexibility. Lower rates than unsecured.",

    heroEyebrow: "HELOC",
    heroHeadline: "Use the equity you've built at home to fund your business.",
    heroSubhead:
      "A Home Equity Line of Credit gives you access to $50K–$500K at some of the lowest rates available. Draw what you need, when you need it. Pay interest only on what you use.",
    applyProductValue: "HELOC",

    keyFacts: [
      { label: "Lines", value: "$50K – $500K" },
      { label: "Terms", value: "10-yr draw, 20-yr repayment" },
      { label: "Type", value: "Revolving line of credit" },
      { label: "Closing time", value: "21 – 45 days" },
    ],

    useCases: [
      "Inject capital into a growing business",
      "Consolidate higher-interest business debt",
      "Buy equipment without dipping into operating cash",
      "Acquire another small business",
      "Cover a major renovation or expansion",
      "Build a reserve for opportunistic moves",
    ],

    requirements: [
      "Own a home with sufficient equity (typically 20%+ equity remaining after the line)",
      "Stable income — W-2, self-employed, or business income",
      "Home appraisal required",
      "No credit score required",
    ],

    why: [
      {
        title: "Lower rates than unsecured",
        desc: "Because your home secures the line, rates are typically much lower than a working capital loan.",
      },
      {
        title: "Pay interest only on what you draw",
        desc: "Open the line now, draw months later. You only pay interest on the money you actually use.",
      },
      {
        title: "Refresh as you repay",
        desc: "Pay down the balance and the line refreshes — capital you can tap whenever you need it for the next 10 years.",
      },
    ],

    faqs: [
      {
        q: "Is a HELOC a good idea for a business owner?",
        a: "It can be — if you have meaningful equity and a clear plan for the capital. HELOC rates are typically much lower than unsecured business loans. The risk: your home secures the line, so missed payments put your home at risk.",
      },
      {
        q: "How much can I borrow?",
        a: "Most lenders will let you borrow up to 80–85% of your home's appraised value, minus your existing mortgage balance. Our team will calculate your exact eligibility on the call.",
      },
      {
        q: "How long does a HELOC take to close?",
        a: "Typically 21–45 days because we need a property appraisal and title work. Faster than a refinance, slower than working capital.",
      },
      {
        q: "Can I use HELOC funds for any purpose?",
        a: "Yes — once the line is open, the funds can be used for any business or personal purpose. We see clients use it most often for business expansion, equipment, and debt consolidation.",
      },
    ],
  },

  "invoice-factoring": {
    slug: "invoice-factoring",
    homeTitle: "Invoice Factoring",
    homeDesc: "Turn unpaid invoices into same-day cash. Advance up to 95% on B2B receivables.",
    icon: "FileText",
    seoTitle: "Invoice Factoring & A/R Financing | Capless Funding",
    seoDescription: "Turn unpaid invoices into same-day cash. Advance up to 95% on B2B receivables. No personal guarantee. Perfect for businesses with net-30/60/90 terms.",

    heroEyebrow: "Invoice Factoring",
    heroHeadline: "Stop waiting on invoices. Get paid today.",
    heroSubhead:
      "Submit a B2B invoice, get up to 95% of the value wired the same day. We collect from your customer; you keep growing. No personal guarantee required.",
    applyProductValue: "Invoice Factoring",

    keyFacts: [
      { label: "Advance rate", value: "Up to 95% of invoice value" },
      { label: "Funding speed", value: "Same day on submitted invoices" },
      { label: "Customer credit", value: "Based on your customers, not you" },
      { label: "Personal guarantee", value: "Not required" },
    ],

    useCases: [
      "B2B services waiting on net-30/60/90 terms",
      "Trucking & logistics with slow-paying brokers",
      "Manufacturing with large invoice cycles",
      "Staffing agencies paying weekly payroll",
      "Government contractors with long payment terms",
      "Anyone tired of chasing accounts receivable",
    ],

    requirements: [
      "B2B business (you invoice other businesses)",
      "Creditworthy customers (we check their credit, not just yours)",
      "Invoices for completed work or delivered goods",
      "Minimum invoice volume varies by program",
    ],

    why: [
      {
        title: "Approval is about your customers",
        desc: "Even if your own credit isn't perfect, you can qualify if your customers (the ones who owe you) are creditworthy.",
      },
      {
        title: "Funds in your account same day",
        desc: "Submit a verified invoice in the morning, see funds in your operating account before close of business.",
      },
      {
        title: "Scales with your sales",
        desc: "The more you invoice, the more capital you can access. No fixed credit limit holding you back.",
      },
    ],

    faqs: [
      {
        q: "How does invoice factoring work?",
        a: "You assign us an invoice your customer owes. We advance you up to 95% of the value immediately. When your customer pays us (per their normal terms), we send you the remaining balance, minus our small fee.",
      },
      {
        q: "What does it cost?",
        a: "Factoring fees typically run 1–4% per 30 days the invoice is outstanding. The exact rate depends on volume, your customers' credit, and industry. We'll quote you upfront — no surprises.",
      },
      {
        q: "Will my customers know I'm using factoring?",
        a: "Yes — your customers send their payment to us instead of you. We handle that handoff professionally. Many B2B customers are familiar with factoring and find it routine.",
      },
      {
        q: "Do I have to factor every invoice?",
        a: "No. We offer 'spot factoring' (one invoice at a time, your choice) and 'whole-ledger' programs (all invoices, lower rates). You pick what fits.",
      },
    ],
  },

  "line-of-credit": {
    slug: "line-of-credit",
    homeTitle: "Business Line of Credit",
    homeDesc: "Revolving capital up to $1M. Pay interest only on what you draw.",
    icon: "Briefcase",
    seoTitle: "Business Line of Credit | Capless Funding",
    seoDescription: "Revolving business line of credit up to $1M. Draw what you need, pay interest only on what you use. Refreshes on repayment.",

    heroEyebrow: "Business Line of Credit",
    heroHeadline: "Capital on standby — for whenever you need it.",
    heroSubhead:
      "Open a revolving line of credit up to $1M today. Draw funds whenever you need them, pay interest only on what you use, and refresh the line as you repay.",
    applyProductValue: "Business Line of Credit",

    keyFacts: [
      { label: "Lines", value: "$10K – $1M" },
      { label: "Type", value: "Revolving — refresh on repayment" },
      { label: "Interest", value: "Only on what you draw" },
      { label: "Funding speed", value: "Draws hit your account in 1–2 hours" },
    ],

    useCases: [
      "Cover seasonal inventory swings",
      "Bridge accounts receivable gaps",
      "Cash on standby for opportunistic moves",
      "Smooth out lumpy cash flow",
      "Cover unexpected expenses without scrambling",
      "Take on a new contract you'd otherwise have to pass on",
    ],

    requirements: [
      "1+ year in business",
      "$50,000+ in monthly revenue",
      "Business checking account in good standing",
      "No credit score required",
    ],

    why: [
      {
        title: "Open it now, use it later",
        desc: "Most lines sit unused — that's a feature, not a bug. You're paying nothing until you draw.",
      },
      {
        title: "Faster than a loan",
        desc: "Once the line is set up, draws happen in real time — funds hit your account in 1–2 hours, no re-application.",
      },
      {
        title: "Builds business credit history",
        desc: "A line of credit with on-time draws and repayments builds the business credit profile that unlocks bigger funding later.",
      },
    ],

    faqs: [
      {
        q: "How is a line of credit different from a term loan?",
        a: "A term loan gives you a lump sum upfront with fixed payments. A line of credit is more flexible — you only borrow (and pay interest on) what you actually use, and the line refreshes as you repay.",
      },
      {
        q: "What does it cost to keep a line open?",
        a: "Most lines have no monthly fee — you pay only when you draw. Some larger lines have a small unused-line fee (typically 0.25–0.50% per year).",
      },
      {
        q: "How fast can I draw funds?",
        a: "Once your line is approved and set up, draws are typically wired to your business checking in 1–2 business hours.",
      },
      {
        q: "Can I have a line and a working capital loan at the same time?",
        a: "Yes — many of our clients pair a line of credit for ongoing flexibility with a term loan for a specific big move (equipment, expansion, etc.).",
      },
    ],
  },
};

export const PRODUCT_ORDER = [
  "working-capital",
  "equipment-financing",
  "sba-loans",
  "heloc",
  "invoice-factoring",
  "line-of-credit",
];

export const PRODUCT_LIST = PRODUCT_ORDER.map((slug) => PRODUCTS[slug]);
